import React from 'react'
import { ABSpinner } from '@abroad/components'
import {
  getExperienceScenario,
  getStatusBadgeMeta,
  formatStartsLong,
  formatUSD,
  pluralize,
  buildOverviewReadinessCards,
  buildOverviewAttentionRows,
  getOverviewPublishBlockers,
  getOverviewProgramDates,
  getOverviewCapacity,
  formatLastEditedLabel,
} from './experienceStatus'
import { UTCFormatDateShort } from '../../utils/date'

const ReadyCard = ({ card, onOpen }) => (
  <div className={`experience-ready-card ${card.kind}`}>
    <div className='experience-ready-icon'>{card.icon}</div>
    <div>
      <span>{card.label}</span>
      <b>{card.title}</b>
      <small>{card.small}</small>
    </div>
    {card.cta ? (
      <button
        type='button'
        className='experience-linkbtn'
        onClick={() => onOpen(card.stage, card.target)}>
        {card.cta}
      </button>
    ) : null}
  </div>
)

const Stat = ({ label, value, small }) => (
  <div className='experience-stat'>
    <span>{label}</span>
    <b>{value}</b>
    {small ? <small>{small}</small> : null}
  </div>
)

const NextStep = ({ n, title, small, cta, onClick }) => (
  <button type='button' className='experience-next-step' onClick={onClick}>
    <span className='experience-stepno'>{n}</span>
    <div>
      <b>{title}</b>
      <small>{small}</small>
    </div>
    <span className='experience-step-cta'>{cta} →</span>
  </button>
)

const SnapshotStats = ({ summary, scenario, capacity }) => {
  if (!summary) {
    return null
  }
  const roster = summary.totalParticipants
  const isLive = scenario === 'live'
  const isCompleted = scenario === 'completed'
  const isDraft = scenario === 'draft'

  if (isCompleted) {
    return (
      <div className='experience-sumrow'>
        <Stat
          label='Participants'
          value={roster != null ? String(roster) : '—'}
          small={
            roster != null ? `${roster} places filled` : 'historical record'
          }
        />
        <Stat
          label='Collected'
          value={formatUSD(summary.collectedAmount)}
          small='final settled amount'
        />
        <Stat
          label='Outstanding'
          value={formatUSD(summary.outstandingAmount)}
          small={
            Number(summary.outstandingAmount) > 0
              ? `${summary.outstandingPeopleCount || 0} ${pluralize(
                  summary.outstandingPeopleCount,
                  'person',
                  'people',
                )}`
              : 'all balances closed'
          }
        />
        <Stat
          label='Documents'
          value={
            Number(summary.documentsMissingCount) > 0
              ? String(summary.documentsMissingCount)
              : 'Complete'
          }
          small='historical record retained'
        />
      </div>
    )
  }

  const rosterSmall = isDraft
    ? 'manually added participants'
    : capacity != null
    ? `of ${capacity} ${isLive ? 'participants' : 'places'}`
    : isLive
    ? 'participants'
    : 'places'

  return (
    <div className='experience-sumrow'>
      <Stat
        label='On the roster'
        value={roster != null ? String(roster) : '—'}
        small={rosterSmall}
      />
      <Stat
        label='Collected'
        value={formatUSD(summary.collectedAmount)}
        small={
          summary.totalAmount != null
            ? `of ${formatUSD(summary.totalAmount)}`
            : 'this experience'
        }
      />
      <Stat
        label='Outstanding'
        value={formatUSD(summary.outstandingAmount)}
        small={
          summary.outstandingPeopleCount != null
            ? `${summary.outstandingPeopleCount} ${pluralize(
                summary.outstandingPeopleCount,
                'person',
                'people',
              )}`
            : 'balances due'
        }
      />
      <Stat
        label='Documents missing'
        value={
          summary.documentsMissingCount != null
            ? String(summary.documentsMissingCount)
            : '—'
        }
        small={
          summary.documentsMissingPeopleCount != null
            ? `across ${summary.documentsMissingPeopleCount} ${pluralize(
                summary.documentsMissingPeopleCount,
                'person',
                'people',
              )}`
            : 'across roster'
        }
      />
    </div>
  )
}

const ReadinessSection = ({ cards, onOpen }) => {
  if (!cards?.length) return null
  return (
    <>
      <h3 className='experience-eyebrow-title'>Readiness</h3>
      <div className='experience-readiness-grid'>
        {cards.map((card) => (
          <ReadyCard key={card.key} card={card} onOpen={onOpen} />
        ))}
      </div>
    </>
  )
}

const QuestOverview = ({
  questDetail,
  overview,
  prepReadiness = null,
  overviewLoading = false,
  overviewError = null,
  onRetryOverview,
  isNewQuest,
  isNewlyCopied = false,
  copiedFromTitle = '',
  onOpenBuild,
  onOpenManage,
  onCopyExperience,
}) => {
  const scenario = getExperienceScenario(questDetail, {
    isNewQuest,
    isNewlyCopied,
    overview,
  })
  const statusMeta = getStatusBadgeMeta(questDetail, {
    isNewQuest,
    isNewlyCopied,
    overview,
  })
  const experience = overview?.experience || {}
  const title =
    experience.name ||
    experience.title ||
    questDetail?.name ||
    questDetail?.title ||
    'Start a new experience'
  const { start, end } = getOverviewProgramDates(overview, questDetail)
  const startsLong = formatStartsLong(start)
  const endLong = formatStartsLong(end)
  const startShort = start ? UTCFormatDateShort(start) : null
  const endShort = end ? UTCFormatDateShort(end) : null
  const capacity = getOverviewCapacity(overview, questDetail)
  const readinessCards = buildOverviewReadinessCards(
    overview,
    scenario,
    questDetail,
  )
  const attentionRows = buildOverviewAttentionRows(
    overview,
    scenario,
    prepReadiness,
  )
  const publishBlockers = getOverviewPublishBlockers(overview)
  const summary = overview?.summary ?? null
  const sourceTitle = copiedFromTitle || 'another experience'
  const isVisible = experience.isVisible !== false
  const lastEdited = formatLastEditedLabel(
    experience.updatedAt || questDetail?.updatedAt,
  )

  const openBuild = (stage, target) => {
    if (typeof onOpenBuild === 'function') {
      onOpenBuild(stage, target)
    }
  }

  const openManage = (target) => {
    if (typeof onOpenManage === 'function') {
      onOpenManage(target)
    }
  }

  // New experience (no questId) — no overview request.
  if (scenario === 'new') {
    return (
      <div className='experience-overview'>
        <div className='experience-overview-head'>
          <div>
            <h2>Start a new experience</h2>
            <p>
              This draft exists, but nothing has been configured or published
              yet.
            </p>
          </div>
          <button
            type='button'
            className='btn-navy'
            onClick={() => openBuild('setup', 'setup-name')}>
            Start setup →
          </button>
        </div>
        <div className='experience-overview-callout'>
          <div className='experience-callout-icon'>1</div>
          <div>
            <h3>Start with the minimum needed for a useful draft</h3>
            <p>
              Name it, choose dates/programme, add price and the core Prosperity
              page information. Enrollment and departure preparation can follow
              later.
            </p>
          </div>
          <button
            type='button'
            className='btn-outline-experience'
            onClick={() => openBuild('setup')}>
            Open Build
          </button>
        </div>
        <ReadinessSection cards={readinessCards} onOpen={openBuild} />
        <h3 className='experience-eyebrow-title'>Recommended next steps</h3>
        <div className='experience-next-steps'>
          <NextStep
            n={1}
            title='Name & summary'
            small='Give the team an internal name and the public title.'
            cta='Start'
            onClick={() => openBuild('setup', 'setup-name')}
          />
          <NextStep
            n={2}
            title='Programme, dates & price'
            small='These unlock duration, lifecycle status and the investment card.'
            cta='Add'
            onClick={() => openBuild('setup', 'setup-dates')}
          />
          <NextStep
            n={3}
            title='Build the Prosperity page'
            small='Hero, inclusions, faculty, journey, FAQs and interest capture.'
            cta='Build'
            onClick={() => openBuild('sell', 'content-hero')}
          />
        </div>
        <h3 className='experience-eyebrow-title'>Operations</h3>
        <div className='experience-zero-snapshot'>
          <b>Nothing to manage yet</b>
          <span>
            Participants, payments and enquiries appear after the experience is
            published and people begin interacting with it.
          </span>
        </div>
      </div>
    )
  }

  if (overviewLoading && !overview) {
    return (
      <div className='experience-overview'>
        <ABSpinner />
      </div>
    )
  }

  if (overviewError && !overview) {
    return (
      <div className='experience-overview'>
        <div className='experience-zero-snapshot'>
          <b>Could not load overview</b>
          <span>
            {overviewError?.message ||
              'Something went wrong loading this experience overview.'}
          </span>
          {typeof onRetryOverview === 'function' ? (
            <button
              type='button'
              className='btn-navy mt-15px'
              onClick={onRetryOverview}>
              Retry
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  if (scenario === 'copied') {
    return (
      <div className='experience-overview'>
        <div className='experience-overview-head'>
          <div>
            <h2>{title}</h2>
            <p>
              Draft · copied from {sourceTitle}. Review dates, leaders and
              anything unique before publishing.
            </p>
          </div>
          <button
            type='button'
            className='btn-navy'
            onClick={() => openBuild('setup', 'setup-name')}>
            Review copied details →
          </button>
        </div>
        <div className='experience-overview-callout'>
          <div className='experience-callout-icon'>↗</div>
          <div>
            <h3>Content was copied; this is still a new draft</h3>
            <p>
              Prosperity page content, enrollment and participant prep came from{' '}
              {sourceTitle}. Participants, payments and inquiries were not
              copied.
            </p>
          </div>
          <button
            type='button'
            className='btn-outline-experience'
            onClick={() => openBuild('setup')}>
            Open Build
          </button>
        </div>
        <ReadinessSection cards={readinessCards} onOpen={openBuild} />
        <h3 className='experience-eyebrow-title'>Recommended next steps</h3>
        <div className='experience-next-steps'>
          <NextStep
            n={1}
            title='Rename and set dates'
            small='The copy still carries the source name and calendar.'
            cta='Update'
            onClick={() => openBuild('setup', 'setup-name')}
          />
          <NextStep
            n={2}
            title='Check price and leaders'
            small='Investment and host emails often change between runs.'
            cta='Review'
            onClick={() => openBuild('setup', 'setup-price')}
          />
          <NextStep
            n={3}
            title='Scan Prosperity page content'
            small='Journey, faculty and FAQs copied across; edit what should differ.'
            cta='Review'
            onClick={() => openBuild('sell', 'content-hero')}
          />
        </div>
        <h3 className='experience-eyebrow-title'>Operations</h3>
        <div className='experience-zero-snapshot'>
          <b>Nothing to manage yet</b>
          <span>
            The roster, payments and inquiries belong to the original experience
            and start empty here.
          </span>
        </div>
      </div>
    )
  }

  if (scenario === 'draft') {
    const hasBlockers = publishBlockers.length > 0
    return (
      <div className='experience-overview'>
        <div className='experience-overview-head'>
          <div>
            <h2>{title}</h2>
            <p>
              Draft
              {lastEdited ? ` · last edited ${lastEdited}` : ''}.{' '}
              {hasBlockers
                ? 'Finish the Prosperity page blockers before publishing.'
                : 'Ready to publish when you are.'}
            </p>
          </div>
          <button
            type='button'
            className='btn-navy'
            onClick={() =>
              openBuild(
                hasBlockers ? publishBlockers[0].stage : 'setup',
                hasBlockers ? publishBlockers[0].target : 'setup-name',
              )
            }>
            Continue editing →
          </button>
        </div>
        <ReadinessSection cards={readinessCards} onOpen={openBuild} />
        <h3 className='experience-eyebrow-title'>What blocks publish</h3>
        {hasBlockers ? (
          <div className='experience-next-steps'>
            {publishBlockers.slice(0, 5).map((blocker, idx) => (
              <NextStep
                key={`${blocker.title}-${idx}`}
                n={idx + 1}
                title={blocker.title}
                small={blocker.small}
                cta='Fix'
                onClick={() => openBuild(blocker.stage, blocker.target)}
              />
            ))}
          </div>
        ) : (
          <div className='experience-zero-snapshot'>
            <b>Nothing blocks publishing</b>
            <span>
              Prosperity page and enrollment required content are complete. You
              can publish when ready.
            </span>
          </div>
        )}
        <h3 className='experience-eyebrow-title'>Operations</h3>
        {summary ? (
          <>
            <p className='experience-hint-top'>
              This draft already has roster or payment activity.
            </p>
            <SnapshotStats
              summary={summary}
              scenario={scenario}
              capacity={capacity}
            />
          </>
        ) : (
          <div className='experience-zero-snapshot'>
            <b>No live activity while this is a draft</b>
            <span>
              Manage unlocks after publishing. Interest submissions,
              participants and payments will then appear there.
            </span>
          </div>
        )}
      </div>
    )
  }

  if (scenario === 'completed') {
    return (
      <div className='experience-overview'>
        <div className='experience-overview-head'>
          <div>
            <h2>{title}</h2>
            <p>
              {endLong ? `Completed ${endLong}. ` : 'Completed. '}
              Setup work is no longer the primary concern; the record is now
              historical and reusable.
            </p>
          </div>
          <button
            type='button'
            className='btn-navy'
            onClick={() => openManage('manage-participants')}>
            View records →
          </button>
        </div>
        <div className='experience-overview-callout'>
          <div className='experience-callout-icon'>✓</div>
          <div>
            <h3>Experience completed</h3>
            <p>
              Participant, payment and enquiry records remain available. Content
              can still be reviewed or copied into a new experience.
            </p>
          </div>
          {typeof onCopyExperience === 'function' ? (
            <button
              type='button'
              className='btn-outline-experience'
              onClick={onCopyExperience}>
              Copy into a new experience
            </button>
          ) : null}
        </div>
        <h3 className='experience-eyebrow-title'>Final snapshot</h3>
        <SnapshotStats
          summary={summary}
          scenario={scenario}
          capacity={capacity}
        />
        <div className='experience-overview-grid'>
          <section className='experience-overview-card'>
            <div className='experience-card-head'>
              <div>
                <span className='experience-eyebrow'>Lifecycle</span>
                <h3>Closed</h3>
              </div>
            </div>
            <div className='experience-status-line'>
              <span>Enrollment</span>
              <b>Closed</b>
            </div>
            <div className='experience-status-line'>
              <span>Trip</span>
              <b>Completed</b>
            </div>
            <div className='experience-status-line'>
              <span>Prosperity page</span>
              <b>Retained</b>
            </div>
            <div className='experience-status-line'>
              <span>Records</span>
              <b>Available</b>
            </div>
          </section>
          <section className='experience-overview-card'>
            <div className='experience-card-head'>
              <div>
                <span className='experience-eyebrow'>Reuse</span>
                <h3>Use what worked</h3>
              </div>
            </div>
            <p className='experience-hint-top'>
              A completed experience is a useful source for the next run.
            </p>
            <div className='experience-quick-actions'>
              {typeof onCopyExperience === 'function' ? (
                <button
                  type='button'
                  className='btn-outline-experience sm'
                  onClick={onCopyExperience}>
                  Copy into a new experience
                </button>
              ) : null}
              <button
                type='button'
                className='btn-outline-experience sm'
                onClick={() => openBuild('sell')}>
                Review content
              </button>
            </div>
          </section>
        </div>
      </div>
    )
  }

  // upcoming | live — Published · Upcoming / Published · In progress
  const isLive = scenario === 'live'
  const prosperityStatus = !isVisible
    ? 'Hidden'
    : experience.isPublished
    ? 'Live'
    : 'Draft'
  const rosterCount =
    summary?.totalParticipants != null
      ? Number(summary.totalParticipants)
      : null
  const capacityLabel =
    rosterCount != null && capacity != null
      ? `${rosterCount} / ${capacity}`
      : rosterCount != null
      ? String(rosterCount)
      : capacity != null
      ? `— / ${capacity}`
      : '—'

  return (
    <div className='experience-overview'>
      <div className='experience-overview-head'>
        <div>
          <h2>{title}</h2>
          <p>
            {isLive
              ? 'In progress now. The overview prioritizes operational exceptions over setup work.'
              : `Published${
                  startsLong ? ` · starts ${startsLong}` : ''
                }. The Prosperity page and enrollment are ready; preparation is now the focus.`}
          </p>
        </div>
        <button
          type='button'
          className='btn-navy'
          onClick={() => openManage('manage-participants')}>
          {isLive ? 'Manage experience →' : 'Manage participants →'}
        </button>
      </div>
      <ReadinessSection cards={readinessCards} onOpen={openBuild} />
      <h3 className='experience-eyebrow-title'>
        {isLive ? 'Live snapshot' : 'Current snapshot'}
      </h3>
      {summary ? (
        <SnapshotStats
          summary={summary}
          scenario={scenario}
          capacity={capacity}
        />
      ) : (
        <div className='experience-sumrow'>
          <Stat
            label='On the roster'
            value='—'
            small={
              capacity != null
                ? `of ${capacity} ${isLive ? 'participants' : 'places'}`
                : isLive
                ? 'participants'
                : 'places'
            }
          />
          <Stat label='Collected' value='—' small='this experience' />
          <Stat label='Outstanding' value='—' small='balances due' />
          <Stat label='Documents missing' value='—' small='across roster' />
        </div>
      )}
      <div className='experience-overview-grid'>
        {attentionRows.length > 0 ? (
          <section className='experience-overview-card experience-attention-card'>
            <div className='experience-card-head'>
              <div>
                <span className='experience-eyebrow'>Needs attention</span>
                <h3>{isLive ? 'Right now' : 'Before departure'}</h3>
              </div>
              <div className='experience-card-head-actions'>
                <span className='experience-count-badge'>
                  {attentionRows.length}
                </span>
              </div>
            </div>
            {attentionRows.map((row) => (
              <button
                key={row.key}
                type='button'
                className='experience-attention-row'
                onClick={() => {
                  if (row.open?.mode === 'manage') {
                    openManage(row.open.target)
                  } else {
                    openBuild(row.open.stage, row.open.target)
                  }
                }}>
                <span
                  className={`experience-att-dot${
                    row.tone === 'danger' ? ' danger' : ''
                  }`}
                />
                <div>
                  <b>{row.title}</b>
                  <small>{row.detail}</small>
                </div>
                <span>{row.cta}</span>
              </button>
            ))}
          </section>
        ) : null}
        <section className='experience-overview-card'>
          <div className='experience-card-head'>
            <div>
              <span className='experience-eyebrow'>Experience status</span>
              <h3>{statusMeta.headerStatus}</h3>
            </div>
          </div>
          <div className='experience-status-line'>
            <span>Prosperity page</span>
            <b>{prosperityStatus}</b>
          </div>
          <div className='experience-status-line'>
            <span>Enrollment</span>
            <b>Open</b>
          </div>
          {isLive ? (
            <>
              <div className='experience-status-line'>
                <span>Starts</span>
                <b>{startShort || '—'}</b>
              </div>
              <div className='experience-status-line'>
                <span>Ends</span>
                <b>{endShort || '—'}</b>
              </div>
            </>
          ) : (
            <>
              <div className='experience-status-line'>
                <span>Starts</span>
                <b>{startShort || '—'}</b>
              </div>
              <div className='experience-status-line'>
                <span>Capacity</span>
                <b>{capacityLabel}</b>
              </div>
            </>
          )}
          <div className='experience-quick-actions'>
            <button
              type='button'
              className='btn-outline-experience sm'
              onClick={() => openManage('manage-participants')}>
              {isLive ? 'Participants' : 'Manage'}
            </button>
            <button
              type='button'
              className='btn-outline-experience sm'
              onClick={() => openBuild(isLive ? 'sell' : 'travel')}>
              {isLive ? 'Build content' : 'Prep'}
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}

export default QuestOverview
